import { Extension, type Editor } from '@tiptap/core';
import { Plugin, PluginKey, type Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet, type EditorView } from '@tiptap/pm/view';
import { useEffect } from 'react';

const FADE_DURATION_MS = 400;
const FADE_TRANSITION = `opacity ${FADE_DURATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`;

export const STREAMED_TEXT_FADE_PLUGIN_KEY = new PluginKey<StreamedTextFadeState>(
  'streamedTextFade'
);

type FadePhase = 'hidden' | 'visible';

interface FadeEntry {
  id: number;
  blockIndex: number;
  phase: FadePhase;
}

interface StreamedTextFadeState {
  active: boolean;
  /**
   * True between activation and the first `docChanged` transaction. That first
   * change is the first streamed chunk: it replaces whatever was on screen
   * (an empty scaffold or, on regenerate, the previous note), so every block it
   * renders is new stream content and must fade regardless of the pre-stream
   * block count.
   */
  awaitingFirstChange: boolean;
  seenBlockCount: number;
  nextId: number;
  entries: FadeEntry[];
}

type StreamedTextFadeMeta =
  | { type: 'set-active'; active: boolean }
  | { type: 'show'; ids: number[] }
  | { type: 'remove'; ids: number[] };

const textBlockPositions = (doc: Transaction['doc']) => {
  if (
    doc.childCount === 1 &&
    doc.firstChild?.isTextblock &&
    doc.firstChild.content.size === 0
  ) {
    return [];
  }

  const positions: Array<{ from: number; to: number }> = [];
  doc.descendants((node, from) => {
    if (!node.isTextblock) return true;
    positions.push({ from, to: from + node.nodeSize });
    return false;
  });
  return positions;
};

const meaningfulBlockCount = (doc: Transaction['doc']) => textBlockPositions(doc).length;

const applyMeta = (
  state: StreamedTextFadeState,
  meta: StreamedTextFadeMeta,
  tr: Transaction
): StreamedTextFadeState => {
  if (meta.type === 'set-active') {
    if (meta.active === state.active) return state;
    return {
      active: meta.active,
      awaitingFirstChange: meta.active,
      seenBlockCount: meaningfulBlockCount(tr.doc),
      nextId: state.nextId,
      entries: [],
    };
  }

  const ids = new Set(meta.ids);
  if (meta.type === 'show') {
    return {
      ...state,
      entries: state.entries.map((entry) =>
        ids.has(entry.id) ? { ...entry, phase: 'visible' } : entry
      ),
    };
  }

  return {
    ...state,
    entries: state.entries.filter((entry) => !ids.has(entry.id)),
  };
};

const applyDocumentChange = (
  state: StreamedTextFadeState,
  tr: Transaction
): StreamedTextFadeState => {
  if (!state.active) return state;

  const blockCount = meaningfulBlockCount(tr.doc);
  const seenBlockCount = state.awaitingFirstChange ? 0 : state.seenBlockCount;

  const entries = state.entries.filter((entry) => entry.blockIndex < blockCount);
  let nextId = state.nextId;
  for (let blockIndex = seenBlockCount; blockIndex < blockCount; blockIndex += 1) {
    entries.push({ id: nextId, blockIndex, phase: 'hidden' });
    nextId += 1;
  }

  return {
    ...state,
    awaitingFirstChange: false,
    // Tracks the current count rather than a monotonic max: when a partial
    // markdown re-parse transiently shrinks the block count (a line briefly
    // reads as one block before a `#`/`>`/list marker splits it), the blocks
    // that reappear are re-rendered stream content and fade like any other new
    // block instead of popping in at full opacity.
    seenBlockCount: blockCount,
    nextId,
    entries,
  };
};

const decorationsForState = (
  doc: Transaction['doc'],
  state: StreamedTextFadeState
): DecorationSet => {
  if (state.entries.length === 0) return DecorationSet.empty;

  const blockPositions = textBlockPositions(doc);
  const decorations = state.entries.flatMap((entry) => {
    // Index-based mapping tradeoff: a mid-document block split/insert shifts
    // indices, so a pending hidden entry can briefly land on an already-visible
    // block and flash it to opacity 0 for one frame. Streams append in
    // practice, so this is accepted over tracking node identity.
    const position = blockPositions[entry.blockIndex];
    if (!position) return [];
    return [
      Decoration.node(
        position.from,
        position.to,
        {
          style: `opacity: ${entry.phase === 'hidden' ? '0' : '1'}; transition: ${FADE_TRANSITION};`,
        },
        { key: `streamed-text-fade-${entry.id}` }
      ),
    ];
  });

  return DecorationSet.create(doc, decorations);
};

const dispatchMeta = (view: EditorView, meta: StreamedTextFadeMeta) => {
  view.dispatch(view.state.tr.setMeta(STREAMED_TEXT_FADE_PLUGIN_KEY, meta));
};

/**
 * Decoration-only streamed-text fade. It remembers text blocks already
 * seen in the current stream, so the full-document replacement used for each
 * chunk cannot re-animate them. Activation is separate from content sync: the
 * controlled apply path has no knowledge of this presentation layer.
 */
export const createStreamedTextFadePlugin = () =>
  new Plugin<StreamedTextFadeState>({
    key: STREAMED_TEXT_FADE_PLUGIN_KEY,
    state: {
      init: (_, state) => ({
        active: false,
        awaitingFirstChange: false,
        seenBlockCount: meaningfulBlockCount(state.doc),
        nextId: 0,
        entries: [],
      }),
      apply: (tr, state) => {
        const meta = tr.getMeta(STREAMED_TEXT_FADE_PLUGIN_KEY) as
          | StreamedTextFadeMeta
          | undefined;
        const withMeta = meta ? applyMeta(state, meta, tr) : state;
        return tr.docChanged ? applyDocumentChange(withMeta, tr) : withMeta;
      },
    },
    props: {
      decorations(state) {
        const pluginState = STREAMED_TEXT_FADE_PLUGIN_KEY.getState(state);
        return pluginState
          ? decorationsForState(state.doc, pluginState)
          : DecorationSet.empty;
      },
    },
    view(view) {
      let destroyed = false;
      const animationFrames = new Map<number, number>();
      const removalTimers = new Map<number, number>();

      const clearScheduled = (id: number) => {
        const win = view.dom.ownerDocument.defaultView;
        const animationFrame = animationFrames.get(id);
        if (animationFrame !== undefined) {
          win?.cancelAnimationFrame(animationFrame);
          animationFrames.delete(id);
        }
        const removalTimer = removalTimers.get(id);
        if (removalTimer !== undefined) {
          win?.clearTimeout(removalTimer);
          removalTimers.delete(id);
        }
      };

      const schedule = () => {
        const state = STREAMED_TEXT_FADE_PLUGIN_KEY.getState(view.state);
        if (!state) return;
        const liveIds = new Set(state.entries.map((entry) => entry.id));
        for (const id of [...animationFrames.keys(), ...removalTimers.keys()]) {
          if (!liveIds.has(id)) clearScheduled(id);
        }

        const win = view.dom.ownerDocument.defaultView;
        if (!win) return;
        for (const entry of state.entries) {
          if (entry.phase !== 'hidden' || animationFrames.has(entry.id)) continue;
          const frame = win.requestAnimationFrame(() => {
            animationFrames.delete(entry.id);
            if (destroyed) return;
            const current = STREAMED_TEXT_FADE_PLUGIN_KEY.getState(view.state);
            if (
              !current?.entries.some(
                ({ id, phase }) => id === entry.id && phase === 'hidden'
              )
            ) {
              return;
            }
            dispatchMeta(view, { type: 'show', ids: [entry.id] });
            const timer = win.setTimeout(() => {
              removalTimers.delete(entry.id);
              if (!destroyed) dispatchMeta(view, { type: 'remove', ids: [entry.id] });
            }, FADE_DURATION_MS);
            removalTimers.set(entry.id, timer);
          });
          animationFrames.set(entry.id, frame);
        }
      };

      schedule();
      return {
        update: schedule,
        destroy() {
          destroyed = true;
          for (const id of [...animationFrames.keys(), ...removalTimers.keys()]) {
            clearScheduled(id);
          }
        },
      };
    },
  });

export const StreamedTextFade = Extension.create({
  name: 'streamedTextFade',
  addProseMirrorPlugins() {
    return [createStreamedTextFadePlugin()];
  },
});

const setStreamedTextFadeActive = (editor: Editor, active: boolean) => {
  if (editor.isDestroyed) return;
  editor.view.dispatch(
    editor.state.tr.setMeta(STREAMED_TEXT_FADE_PLUGIN_KEY, {
      type: 'set-active',
      active,
    } satisfies StreamedTextFadeMeta)
  );
};

/**
 * Opt-in activation seam. Reduced-motion users never activate the plugin, and
 * stream-end deactivates it before the canonical finalize apply runs.
 */
export const useStreamedTextFade = ({
  editor,
  enabled = false,
  isStreaming = false,
}: {
  editor: Editor | null | undefined;
  enabled?: boolean;
  isStreaming?: boolean;
}) => {
  useEffect(() => {
    if (!enabled || !editor || editor.isDestroyed) return;

    const mediaQuery =
      typeof window === 'undefined' || typeof window.matchMedia !== 'function'
        ? null
        : window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () =>
      setStreamedTextFadeActive(editor, isStreaming && !mediaQuery?.matches);

    sync();
    mediaQuery?.addEventListener('change', sync);
    return () => {
      mediaQuery?.removeEventListener('change', sync);
      setStreamedTextFadeActive(editor, false);
    };
  }, [editor, enabled, isStreaming]);
};
